import { forwardRef, useCallback, useId, useRef, type HTMLAttributes, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useControlledState } from "./hooks";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Slider>. Full WAI-ARIA
// slider keyboard pattern (Arrow/PageUp/PageDown/Home/End), pointer
// drag-to-set, and CSS ported letter-for-letter from the vendor's compiled
// bundle and its Slider CSS — new .rc-slider class names. Uses the local
// ./hooks' useControlledState (ported from @datasirpi/hooks, since fully
// removed from this app now) rather than reimplementing controlled/
// uncontrolled merging inline here.
export interface SliderProps extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  label?: ReactNode;
  "aria-label"?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  onChange?: (value: number) => void;
  formatValue?: (value: number) => string;
  showValue?: boolean;
  disabled?: boolean;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(Math.max(n, lo), hi);
}
function quantize(n: number, step: number, min: number) {
  return min + Math.round((n - min) / step) * step;
}

export const Slider = forwardRef<HTMLDivElement, SliderProps>(function Slider(
  {
    label,
    "aria-label": ariaLabel,
    min = 0,
    max = 100,
    step = 1,
    value: controlled,
    defaultValue,
    onChange,
    formatValue,
    showValue,
    disabled = false,
    className,
    id: providedId,
    ...rest
  },
  ref
) {
  const fallbackId = useId();
  const id = providedId ?? fallbackId;
  const labelId = label ? `${id}-label` : undefined;
  const [maybeValue, setValue] = useControlledState<number>({
    ...(controlled !== undefined ? { value: controlled } : {}),
    defaultValue: defaultValue ?? min,
    ...(onChange ? { onChange } : {}),
  });
  const value = maybeValue ?? min;
  const trackRef = useRef<HTMLDivElement | null>(null);

  const commit = useCallback(
    (next: number) => {
      const clamped = clamp(quantize(next, step, min), min, max);
      if (clamped !== value) setValue(clamped);
    },
    [step, min, max, value, setValue]
  );

  function pctOf(n: number) {
    return ((n - min) / (max - min)) * 100;
  }
  function pickFromPointer(clientX: number) {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    commit(min + ratio * (max - min));
  }
  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (disabled) return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pickFromPointer(e.clientX);
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (disabled) return;
    if (e.buttons !== 1) return;
    pickFromPointer(e.clientX);
  }
  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    const big = step * 10;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowUp":
        e.preventDefault();
        commit(value + step);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        e.preventDefault();
        commit(value - step);
        break;
      case "PageUp":
        e.preventDefault();
        commit(value + big);
        break;
      case "PageDown":
        e.preventDefault();
        commit(value - big);
        break;
      case "Home":
        e.preventDefault();
        commit(min);
        break;
      case "End":
        e.preventDefault();
        commit(max);
        break;
    }
  }

  const pct = pctOf(value);
  const formatted = formatValue ? formatValue(value) : String(value);
  const showsReadout = showValue ?? Boolean(label);

  return (
    <div ref={ref} data-disabled={disabled || undefined} className={cn("rc-slider", className)} {...rest}>
      {label || showsReadout ? (
        <div className="rc-slider__header">
          {label ? (
            <span id={labelId} className="rc-slider__label">
              {label}
            </span>
          ) : null}
          {showsReadout ? <span className="rc-slider__value">{formatted}</span> : null}
        </div>
      ) : null}
      <div ref={trackRef} className="rc-slider__track-wrap" onPointerDown={onPointerDown} onPointerMove={onPointerMove}>
        <div className="rc-slider__track">
          <div className="rc-slider__fill" style={{ width: `${pct}%` }} />
        </div>
        <div
          role="slider"
          tabIndex={disabled ? -1 : 0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-valuetext={formatValue ? formatted : undefined}
          aria-labelledby={labelId}
          aria-label={!labelId ? ariaLabel : undefined}
          aria-disabled={disabled || undefined}
          className="rc-slider__thumb"
          style={{ insetInlineStart: `${pct}%` }}
          onKeyDown={onKeyDown}
        />
      </div>
    </div>
  );
});
